/**
 * Game-derived kind resolution (TDD §5.2) — the anti-over-fit seam. A DETERMINISTIC map
 * over the STRUCTURED `genre` field (not a prose regex), with an injectable LLM-judge
 * fallback for ambiguity so a game only ever sees the kinds useful to it.
 */
import type { LevelKindId } from './types.js';

export interface GameDesignIntentLike {
  /** Structural genre (e.g. "auto-runner"), NOT free prose. */
  genre?: string;
  mechanics?: readonly string[];
  brief?: string;
}

/** Injected for ambiguous cases so tests stay pure (no live model call). */
export type KindJudge = (intent: GameDesignIntentLike) => LevelKindId[];

const GENRE_KIND_MAP: Record<string, readonly LevelKindId[]> = {
  'auto-runner': ['spatial-2d'],
  platformer: ['spatial-2d'],
  'action-adventure': ['topdown-2d'],
  'dungeon-crawl': ['topdown-2d'],
  'top-down': ['topdown-2d'],
  'match-3': ['board-grid', 'data-table'],
  'grab-bag': ['data-table'],
  trivia: ['data-table'],
};

const GENRE_ALIASES: Record<string, string> = {
  autorunner: 'auto-runner',
  'auto runner': 'auto-runner',
  match3: 'match-3',
  'match 3': 'match-3',
  grabbag: 'grab-bag',
  'grab bag': 'grab-bag',
};

export function normalizeGenre(genre: string | undefined): string {
  const g = (genre ?? '').trim().toLowerCase();
  return GENRE_ALIASES[g] ?? g;
}

/**
 * Resolve the set of level kinds a game needs from its design intent. Returns the
 * deterministic mapping when the genre is known; otherwise defers to `judge` (or `[]`).
 */
export function resolveKinds(intent: GameDesignIntentLike, judge?: KindJudge): LevelKindId[] {
  const genre = normalizeGenre(intent.genre);
  const mapped = GENRE_KIND_MAP[genre];
  if (mapped) return [...mapped];
  return judge ? judge(intent) : [];
}
